/**
 * Realtime Collaboration Hook
 * 
 * Provides field-level presence tracking and real-time synchronization
 * for collaborative editing using Appwrite Realtime.
 * 
 * Features:
 * - Real-time data synchronization via Appwrite subscriptions
 * - Optimistic updates with offline support
 * - Field editing presence tracking (local only for now)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import appwrite from '../services/appwrite';
import { coerceFieldValues } from '../lib/fieldTypes';
import { usePresence } from './usePresence';

const { getCurrentUser, subscribeToDocument, updateDocument, getDocument, COLLECTIONS } = appwrite;

/**
 * Hook for realtime collaboration on a specific record
 * 
 * @param {string} collectionName - The collection name (e.g., 'quote_requests')
 * @param {string} recordId - The record ID to collaborate on
 * @param {object} initialData - Initial data for the record
 * @returns {object} Collaboration state and methods
 */
export function useRealtimeCollaboration(collectionName, recordId, initialData = {}) {
  const [data, setData] = useState(initialData);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [localChanges, setLocalChanges] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const {
    activeUsers,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  } = usePresence(recordId ? `${collectionName}:${recordId}` : null);

  const unsubscribeRef = useRef(null);
  const debounceTimersRef = useRef({});
  const lastUpdateRef = useRef(null);
  // Unflushed local edits, keyed by field. This is the guard that stops an
  // incoming payload from overwriting what the user is typing, so it has to be
  // accurate *synchronously*: a socket message can land between a keystroke's
  // setState and the render that would mirror that state into a ref. The
  // `localChanges` state is kept in step for rendering and the offline resync.
  const pendingRef = useRef({});
  // The field the user currently has focus in, plus any remote value withheld
  // from it, so a teammate's change can still be applied once focus leaves.
  const focusedFieldRef = useRef(null);
  const deferredRemoteRef = useRef({});
  const typedWhileFocusedRef = useRef(false);
  // $updatedAt of the newest payload applied, so an echo that overtakes a newer
  // one (and carries a pre-edit snapshot of the document) can be dropped.
  const lastAppliedAtRef = useRef(0);

  // Map collection names to Appwrite collection IDs
  const getCollectionId = useCallback((name) => {
    const collectionMap = {
      'quote_requests': COLLECTIONS.QUOTE_REQUESTS,
      'customers': COLLECTIONS.CUSTOMERS,
      'customer_vehicles': COLLECTIONS.CUSTOMER_VEHICLES,
      'quotes': COLLECTIONS.QUOTES,
      'quote_lines': COLLECTIONS.QUOTE_LINES
    };
    return collectionMap[name] || name;
  }, []);

  // Record an edit that has not reached the server yet. Ref first, state second.
  const markPending = useCallback((updates) => {
    pendingRef.current = { ...pendingRef.current, ...updates };
    setLocalChanges(pendingRef.current);
  }, []);

  // Release fields once their write lands — but only those still holding the
  // value that was written. A keystroke that arrived while the request was in
  // flight has its own pending write, and dropping its guard here would let the
  // echo of this (now older) write overwrite the newer text.
  const settlePending = useCallback((written) => {
    const next = { ...pendingRef.current };
    let changed = false;
    Object.entries(written).forEach(([field, value]) => {
      if (field in next && next[field] === value) {
        delete next[field];
        changed = true;
      }
    });
    if (!changed) return;
    pendingRef.current = next;
    setLocalChanges(next);
  }, []);

  // Initialize current user
  useEffect(() => {
    const initUser = async () => {
      const user = await getCurrentUser();
      console.log('👤 Current user loaded:', user);
      setCurrentUser(user);
    };
    initUser();
  }, []);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize realtime subscription
  useEffect(() => {
    if (!recordId || !currentUser) {
      console.log('⏸️ Realtime: Waiting for recordId and currentUser', { recordId, hasUser: !!currentUser });
      return;
    }

    const collectionId = getCollectionId(collectionName);
    console.log('🔌 Realtime: Subscribing to', collectionId, recordId);
    lastAppliedAtRef.current = 0;

    try {
      // Subscribe to document changes
      unsubscribeRef.current = subscribeToDocument(collectionId, recordId, (response) => {
        console.log('📥 Realtime update received:', response.events, response.payload);

        // Appwrite delivers events to every subscriber including the session
        // that made the write, and background writers touch this record while it
        // is open — the AI assessment reports its progress onto the quote
        // request. Every payload is a full document, so one produced before the
        // keystrokes we are still holding carries the pre-edit value of every
        // field; applied blindly, the text being typed jumps back or loses the
        // characters typed since.
        if (response.payload) {
          // Out-of-order echo: an older snapshot arriving after a newer one has
          // nothing fresh in it, only stale values for every field.
          const updatedAt = Date.parse(response.payload.$updatedAt ?? '');
          if (Number.isFinite(updatedAt)) {
            if (updatedAt < lastAppliedAtRef.current) return;
            lastAppliedAtRef.current = updatedAt;
          }

          const incoming = { ...response.payload };

          // Fields edited locally are ours until that edit reaches the server.
          Object.keys(pendingRef.current).forEach(field => { delete incoming[field]; });

          // The field under the cursor is left alone even with nothing pending:
          // its debounced write may have flushed a moment ago and this payload
          // can still predate it. Hold the value and reconcile on blur.
          const focused = focusedFieldRef.current;
          if (focused && focused in incoming) {
            deferredRemoteRef.current[focused] = incoming[focused];
            delete incoming[focused];
          }

          setData(prev => ({
            ...prev,
            ...incoming
          }));
          lastUpdateRef.current = new Date();
        }
      });
      
      console.log('✅ Realtime: Subscription active');
    } catch (err) {
      console.error('❌ Realtime: Subscription failed:', err);
    }

    return () => {
      console.log('🧹 Realtime: Cleaning up subscription');
      if (unsubscribeRef.current && typeof unsubscribeRef.current === 'function') {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [collectionName, recordId, currentUser, getCollectionId]);

  /**
   * Update field value with optimistic updates and debounced server sync
   */
  const updateField = useCallback(async (fieldName, value, debounceMs = 1000) => {
    // Update local state immediately (optimistic update)
    setData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Guard the field against remote echoes until this edit is on the server
    markPending({ [fieldName]: value });
    if (focusedFieldRef.current === fieldName) typedWhileFocusedRef.current = true;

    // Clear any existing debounce timer for this field
    if (debounceTimersRef.current[fieldName]) {
      clearTimeout(debounceTimersRef.current[fieldName]);
    }

    // Debounce the server update
    debounceTimersRef.current[fieldName] = setTimeout(async () => {
      if (!recordId) return;
      
      try {
        setIsSyncing(true);
        const collectionId = getCollectionId(collectionName);
        // Numeric columns are typed in the schema; an input hands back a string
        // and Appwrite rejects the write outright. Coerced here, at the wire,
        // so local state keeps the value exactly as the user typed it.
        await updateDocument(collectionId, recordId, coerceFieldValues(collectionName, { [fieldName]: value }));

        // Clear this field from local changes after successful sync
        settlePending({ [fieldName]: value });

        lastUpdateRef.current = new Date();
      } catch (err) {
        console.error('Failed to sync field update:', err);
        // Keep in local changes for retry
      } finally {
        setIsSyncing(false);
      }
    }, debounceMs);
  }, [recordId, collectionName, getCollectionId, markPending, settlePending]);

  /**
   * Bulk update multiple fields
   */
  const updateFields = useCallback(async (updates, debounceMs = 1000) => {
    // Update local state immediately
    setData(prev => ({
      ...prev,
      ...updates
    }));

    markPending(updates);

    // Clear any existing debounce timer for bulk updates
    if (debounceTimersRef.current._bulk) {
      clearTimeout(debounceTimersRef.current._bulk);
    }

    // Debounce the server update
    debounceTimersRef.current._bulk = setTimeout(async () => {
      if (!recordId) return;
      
      try {
        setIsSyncing(true);
        const collectionId = getCollectionId(collectionName);
        await updateDocument(collectionId, recordId, coerceFieldValues(collectionName, updates));

        // Clear updated fields from local changes
        settlePending(updates);

        lastUpdateRef.current = new Date();
      } catch (err) {
        console.error('Failed to sync bulk update:', err);
      } finally {
        setIsSyncing(false);
      }
    }, debounceMs);
  }, [recordId, collectionName, getCollectionId, markPending, settlePending]);

  /**
   * Presence handlers, wrapped so the hook knows which field holds the cursor.
   */
  const beginFieldEdit = useCallback((fieldName) => {
    focusedFieldRef.current = fieldName;
    typedWhileFocusedRef.current = false;
    delete deferredRemoteRef.current[fieldName];
    trackFieldEdit(fieldName);
  }, [trackFieldEdit]);

  const endFieldEdit = useCallback(() => {
    const field = focusedFieldRef.current;
    const typed = typedWhileFocusedRef.current;
    focusedFieldRef.current = null;
    typedWhileFocusedRef.current = false;

    if (field && field in deferredRemoteRef.current) {
      const remoteValue = deferredRemoteRef.current[field];
      delete deferredRemoteRef.current[field];
      // Take the remote value only if we left the field as we found it. If we
      // typed, ours is the newer value — it is either still pending or already
      // flushed, and the withheld payload may predate it.
      if (!typed && !(field in pendingRef.current)) {
        setData(prev => ({ ...prev, [field]: remoteValue }));
      }
    }

    clearFieldEdit();
  }, [clearFieldEdit]);

  /**
   * Sync local changes when coming back online
   *
   * Watches the offline → online transition only. Depending on `localChanges`
   * here fired on every keystroke: it wrote each character straight to Appwrite,
   * ignoring the debounce, and then cleared the pending guard — so every echo
   * the server sent back landed on the field being typed into.
   */
  const wasOnlineRef = useRef(isOnline);
  useEffect(() => {
    const cameBackOnline = isOnline && !wasOnlineRef.current;
    wasOnlineRef.current = isOnline;
    if (!cameBackOnline || !recordId) return;

    const queued = pendingRef.current;
    if (Object.keys(queued).length === 0) return;

    const syncChanges = async () => {
      try {
        setIsSyncing(true);
        const collectionId = getCollectionId(collectionName);
        await updateDocument(collectionId, recordId, coerceFieldValues(collectionName, queued));
        settlePending(queued);
        lastUpdateRef.current = new Date();
        console.log('✅ Synced local changes after coming back online');
      } catch (err) {
        console.error('Failed to sync local changes:', err);
      } finally {
        setIsSyncing(false);
      }
    };
    syncChanges();
  }, [isOnline, recordId, collectionName, getCollectionId, settlePending]);

  /**
   * Refresh data from server
   */
  const refresh = useCallback(async () => {
    if (!recordId) return;
    
    try {
      setIsSyncing(true);
      const collectionId = getCollectionId(collectionName);
      const doc = await getDocument(collectionId, recordId);
      setData(doc);
      lastUpdateRef.current = new Date();
    } catch (err) {
      console.error('Failed to refresh data:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [recordId, collectionName, getCollectionId]);

  return {
    // Data state
    data,
    localChanges,
    hasLocalChanges: Object.keys(localChanges).length > 0,
    
    // Sync state
    isOnline,
    isSyncing,
    lastUpdate: lastUpdateRef.current,
    
    // Presence state
    activeUsers,
    currentUser,

    // Methods
    updateField,
    updateFields,
    trackFieldEdit: beginFieldEdit,
    clearFieldEdit: endFieldEdit,
    getFieldEditor,
    isCurrentUserEditing,
    refresh
  };
}

export default useRealtimeCollaboration;
